import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * <ta-refresh> keeps server-rendered Tissire blocks in sync with variant changes.
 * The theme's variant picker fetches the re-rendered section and dispatches
 * ThemeEvents.variantUpdate with the new document in event.detail.data.html.
 * This element finds its own counterpart in that document and morphs itself,
 * so all state (prices, links, selected pills) stays server-rendered Liquid.
 */
class TaRefresh extends HTMLElement {
  connectedCallback() {
    this.taSection = this.closest('[id*="ProductInformation-"]');
    this.taSection?.addEventListener(ThemeEvents.variantUpdate, this.taUpdate);
  }

  disconnectedCallback() {
    this.taSection?.removeEventListener(ThemeEvents.variantUpdate, this.taUpdate);
  }

  /** @param {CustomEvent} event */
  taUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html || !this.id) return;
    const next = html.getElementById(this.id);
    if (next) morph(this, next);
  };
}

/**
 * <ta-option-picker> renders enriched option pills (price, per-unit, inches)
 * server-side and proxies clicks to the theme's native variant picker inputs,
 * so all variant logic (fetch, morph, URL, combined listings, cart) stays native.
 * The native picker is only hidden once this element is connected, keeping the
 * page fully usable without JavaScript.
 */
class TaOptionPicker extends TaRefresh {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.taProxyClick);
    // Hide the native picker only when the custom instances cover every
    // product option (they can be split, e.g. Diameter and Height around the
    // design switcher). Partial coverage falls back to the native picker so
    // no option can ever become unselectable.
    TaOptionPicker.taSyncCoverage(this.taSection);
    this.taApplyDefaults();
  }

  /**
   * Opens the page on the block's "default_values" (e.g. 40 cm on a 45 cm base) instead of the
   * first variant, once, and only when the link did not ask for a variant. All but the last
   * native input are checked silently and the last one is clicked, so the native picker reads
   * every checked value and fetches the section a single time.
   */
  taApplyDefaults() {
    const wanted = (this.dataset.defaultValues || '').split(',').map((v) => v.trim()).filter(Boolean);
    if (wanted.length === 0 || TaOptionPicker.taDefaultsDone) return;
    TaOptionPicker.taDefaultsDone = true;
    if (new URLSearchParams(window.location.search).has('variant')) return;

    customElements.whenDefined('variant-picker').then(() =>
      requestAnimationFrame(() => {
        const pills = [...this.querySelectorAll('.ta-op-pill')];
        const inputs = [];
        for (const value of wanted) {
          const pill = pills.find((p) => p.querySelector('.ta-op-pill-value')?.textContent?.trim() === value);
          if (!pill || pill.getAttribute('aria-checked') === 'true') continue;
          if (pill.classList.contains('ta-op-pill--unavailable')) continue;
          const input = this.taSection?.querySelector(
            `variant-picker input[data-option-value-id="${pill.dataset.optionValueId}"]`
          );
          if (input instanceof HTMLInputElement) inputs.push(input);
        }
        const last = inputs.pop();
        if (!last) return;
        for (const input of inputs) input.checked = true;
        // The native picker writes ?variant= from the clicked input alone, which still points at the
        // old size. Once the section comes back, put the variant that was really loaded in the URL.
        this.taSection?.addEventListener(
          ThemeEvents.variantUpdate,
          (event) => {
            const id =
              event.detail?.resource?.id ??
              event.detail?.data?.html?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
            if (!id) return;
            const url = new URL(window.location.href);
            url.searchParams.set('variant', String(id));
            setTimeout(() => history.replaceState({}, '', url.toString()), 0);
          },
          { once: true }
        );
        last.click();
      })
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.taSection) return;
    this.taSection.classList.remove('ta-custom-picker-active');
    TaOptionPicker.taSyncCoverage(this.taSection);
  }

  /** @param {Element | null} section */
  static taSyncCoverage(section) {
    if (!section) return;
    const pickers = [...section.querySelectorAll('ta-option-picker')].filter((p) => p.isConnected);
    if (pickers.length === 0) {
      section.classList.remove('ta-custom-picker-active');
      return;
    }
    const total = Number(pickers[0].dataset.optionsTotal || 0);
    const names = new Set(
      pickers.flatMap((p) => [...p.querySelectorAll('.ta-op-group')].map((g) => g.dataset.optionName))
    );
    section.classList.toggle('ta-custom-picker-active', total > 0 && names.size >= total);
  }

  /** @param {MouseEvent} event */
  taProxyClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const pill = target?.closest('button[data-option-value-id]');
    if (!pill || pill.getAttribute('aria-checked') === 'true') return;

    const input = this.taSection?.querySelector(
      `variant-picker input[data-option-value-id="${pill.dataset.optionValueId}"]`
    );

    if (input instanceof HTMLInputElement) {
      input.click();
    } else {
      // Dropdown style fallback: update the select that holds this option value.
      const option = this.taSection?.querySelector(
        `variant-picker option[data-option-value-id="${pill.dataset.optionValueId}"]`
      );
      if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
        option.parentElement.value = option.value;
        option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
}

// "See all N" / "Show fewer" on the design switcher. The state lives on <html> so it survives
// the morph that follows every variant change.
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ta-designs-toggle]') : null;
  if (!button) return;
  const open = document.documentElement.classList.toggle('ta-designs-open');
  button.setAttribute('aria-expanded', String(open));
});

if (!customElements.get('ta-refresh')) customElements.define('ta-refresh', TaRefresh);
if (!customElements.get('ta-option-picker')) customElements.define('ta-option-picker', TaOptionPicker);
